type HeatmapCell = { 
  x: number; 
  y: number; 
  value: number; 
  songs: Array<{ name: string; artist: string; rank: number; valence: number; energy: number; albumImage?: string; spotifyId?: string }>;
};

type UnifiedGridCell = {
  density: number;
  songs: Array<{ name: string; artist: string; rank: number; valence: number; energy: number; albumImage?: string; spotifyId?: string }>;
};

/**
 * Enhanced Gaussian blur implementation with configurable sigma
 * Similar to scipy.ndimage.gaussian_filter
 */
const gaussianFilter = (grid: number[][], sigma: number = 2.5): number[][] => {
  if (!grid || grid.length === 0 || !grid[0] || grid[0].length === 0) {
    return grid;
  }
  
  const height = grid.length;
  const width = grid[0].length;
  
  // Calculate kernel size based on sigma (typically 6*sigma + 1)
  const kernelSize = Math.max(3, Math.ceil(6 * sigma)); // Increased for smoother results
  // Ensure odd number for proper centering
  const oddKernelSize = kernelSize % 2 === 0 ? kernelSize + 1 : kernelSize;
  const kernel = createGaussianKernel(oddKernelSize, sigma);
  
  const result = Array.from({ length: height }, () => Array(width).fill(0));
  const halfKernel = Math.floor(oddKernelSize / 2);
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      let weightSum = 0;
      
      for (let ky = 0; ky < oddKernelSize; ky++) {
        for (let kx = 0; kx < oddKernelSize; kx++) {
          const sourceY = y + ky - halfKernel;
          const sourceX = x + kx - halfKernel;
          
          // Handle boundaries by clamping (similar to scipy's 'reflect' mode)
          const clampedY = Math.max(0, Math.min(height - 1, sourceY));
          const clampedX = Math.max(0, Math.min(width - 1, sourceX));
          
          const weight = kernel[ky][kx];
          sum += grid[clampedY][clampedX] * weight;
          weightSum += weight;
        }
      }
      
      result[y][x] = weightSum > 0 ? sum / weightSum : 0;
    }
  }
  
  return result;
};

/**
 * Create a normalized Gaussian kernel
 */
const createGaussianKernel = (size: number, sigma: number): number[][] => {
  if (size <= 0 || sigma <= 0) {
    return [[1]]; // Return identity kernel for invalid inputs
  }
  
  const kernel: number[][] = [];
  const center = Math.floor(size / 2);
  let sum = 0;
  
  for (let y = 0; y < size; y++) {
    kernel[y] = [];
    for (let x = 0; x < size; x++) {
      const dx = x - center;
      const dy = y - center;
      const exponent = -(dx * dx + dy * dy) / (2 * sigma * sigma);
      const value = Math.exp(exponent);
      kernel[y][x] = value;
      sum += value;
    }
  }
  
  // Normalize the kernel
  if (sum > 0) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        kernel[y][x] /= sum;
      }
    }
  }
  
  return kernel;
};

export const computeGroupedDensityHeatmap = (
  songData: any[],
  gridSize: number = 80, // Increased even more for ultra-smooth results
  smoothingSigma: number = 4.0, // Much higher sigma for very smooth, organic blending
  rankWeightingFactor: number = 1.0 // 0 = equal weights, 1 = balanced, >1 = more rank influence
): { heatmapCells: HeatmapCell[], unifiedGrid: UnifiedGridCell[][] } => {
  if (!songData || songData.length === 0) {
    return { heatmapCells: [], unifiedGrid: [] };
  }

  // Create unified grid where each cell contains both density and songs
  const unifiedGrid: UnifiedGridCell[][] = Array.from({ length: gridSize }, () =>
    Array.from({ length: gridSize }, () => ({ density: 0, songs: [] }))
  );

  // Improved weighting system that considers both rank and density
  songData.forEach((song, index) => {
    // Convert [0,1] values to [-1,1] space for consistent coordinate system
    const valence = Math.max(-1, Math.min(1, (song.valence || 0) * 2 - 1));
    const energy = Math.max(-1, Math.min(1, (song.energy || 0) * 2 - 1));
    
    // Configurable rank weighting system
    let totalWeight: number;
    
    if (rankWeightingFactor === 0) {
      // Equal weighting: all songs have the same influence
      totalWeight = 1.0;
    } else {
      // Scaled rank weighting based on the factor
      // Base decay rate scales with the factor: higher factor = more rank influence
      const decayRate = 50 / Math.max(0.1, rankWeightingFactor); // Inverse relationship: higher factor = faster decay
      const baseMultiplier = 2 * rankWeightingFactor; // Scale base weight with factor
      
      const rankWeight = Math.exp(-index / decayRate) * baseMultiplier;
      
      // Popularity boost also scales with the factor
      const topSongCount = Math.max(1, Math.floor(10 / Math.max(0.1, rankWeightingFactor))); // Fewer top songs get boost with higher factor
      const boostAmount = 1 + (0.2 * rankWeightingFactor); // Boost scales with factor
      const popularityBoost = index < topSongCount ? boostAmount : 1.0;
      
      totalWeight = rankWeight * popularityBoost;
    }
    
    // Map [-1,1] coordinates to continuous grid positions
    const gridX = ((valence + 1) / 2) * (gridSize - 1);
    const gridY = ((energy + 1) / 2) * (gridSize - 1);
    
    // Use bilinear interpolation to distribute weight smoothly
    const x1 = Math.floor(gridX);
    const x2 = Math.min(x1 + 1, gridSize - 1);
    const y1 = Math.floor(gridY);
    const y2 = Math.min(y1 + 1, gridSize - 1);
    
    const wx = gridX - x1;
    const wy = gridY - y1;
    
    // Song information for tooltips (store original [0,1] values for display)
    // Try multiple possible data structures
    const songInfo = {
      name: song.name || song.trackTitle || song.fullSong?.trackTitle || song.fullSong?.name || 'Unknown Track',
      artist: song.artists?.[0]?.name || song.artist || song.fullSong?.artist || song.fullSong?.artists?.[0]?.name || 'Unknown Artist',
      rank: song.rank || index + 1,
      valence: song.valence || 0,
      energy: song.energy || 0,
      albumImage: song.albumImage || song.album?.images?.[0]?.url || song.fullSong?.album?.images?.[0]?.url || undefined,
      spotifyId: song.spotify_id || song.id || song.fullSong?.id || undefined
    };

    // Distribute weight across 4 neighboring cells using bilinear weights
    const weights = [
      { x: x1, y: y1, weight: totalWeight * (1 - wx) * (1 - wy) },
      { x: x2, y: y1, weight: totalWeight * wx * (1 - wy) },
      { x: x1, y: y2, weight: totalWeight * (1 - wx) * wy },
      { x: x2, y: y2, weight: totalWeight * wx * wy }
    ];

    // Add to unified grid - both density and song information in the same cells
    weights.forEach(({ x, y, weight }) => {
      if (x >= 0 && x < gridSize && y >= 0 && y < gridSize && weight > 0.1) {
        // Add density weight
        unifiedGrid[y][x].density += weight;
        
        // Add song to this cell if weight is significant
        if (weight > 0.05) { // Further lowered threshold to include more songs across all ranks
          unifiedGrid[y][x].songs.push(songInfo);
        }
      }
    });
  });

  // Extract density matrix for Gaussian smoothing
  const densityMatrix: number[][] = unifiedGrid.map(row => row.map(cell => cell.density));

  // Apply Gaussian smoothing with increased sigma for much smoother effect
  const smoothedDensityMatrix = gaussianFilter(densityMatrix, smoothingSigma);

  // Convert the unified grid to heatmap cells with song information
  const heatmapData: HeatmapCell[] = [];
  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < gridSize; x++) {
      // Map grid coordinates to [-1, 1] space for display
      const valence = (x / (gridSize - 1)) * 2 - 1; // Map [0, gridSize-1] to [-1, 1]
      const energy = (y / (gridSize - 1)) * 2 - 1; // Map [0, gridSize-1] to [-1, 1] 
      // Note: No Y-flip here - the matrix stores data in the same coordinate system as the grid
      // The Y-flip happens in the Heatmap component during rendering to match D3's display convention
      
      // Sort songs by rank (best first) and limit to top 3 for tooltip
      const cellSongs = unifiedGrid[y][x].songs
        .sort((a, b) => a.rank - b.rank)
        .slice(0, 3);
      
      heatmapData.push({ 
        x: valence, 
        y: energy, 
        value: smoothedDensityMatrix[y][x], // Use smoothed density
        songs: cellSongs // Use original songs from unified grid
      });
    }
  }

  return { heatmapCells: heatmapData, unifiedGrid };
};

/**
 * Get songs within a radius from the unified grid
 */
export const getSongsInRadius = (
  unifiedGrid: UnifiedGridCell[][],
  valence: number,
  energy: number,
  radius: number = 0.15 // Increased default radius to find more songs
): Array<{ name: string; artist: string; rank: number; valence: number; energy: number; albumImage?: string; spotifyId?: string }> => {
  const gridSize = unifiedGrid.length;
  const songs: Array<{ name: string; artist: string; rank: number; valence: number; energy: number }> = [];
  
  // Convert position to grid coordinates
  const gridX = ((valence + 1) / 2) * (gridSize - 1);
  const gridY = ((energy + 1) / 2) * (gridSize - 1);
  const centerX = Math.floor(gridX);
  const centerY = Math.floor(gridY);
  
  // Calculate grid radius based on the actual grid size
  // For 80x80 grid, 0.05 radius should be about 2 cells (0.05 * 80 / 2 = 2)
  const gridRadius = Math.ceil(radius * gridSize / 2);
  
  // Check surrounding cells
  for (let dy = -gridRadius; dy <= gridRadius; dy++) {
    for (let dx = -gridRadius; dx <= gridRadius; dx++) {
      const x = centerX + dx;
      const y = centerY + dy;
      
      // Check bounds
      if (x >= 0 && x < gridSize && y >= 0 && y < gridSize) {
        const cell = unifiedGrid[y][x];
        
        // Add all songs from this cell
        cell.songs.forEach(song => {
          // Calculate actual distance to filter more precisely
          const songValence = song.valence * 2 - 1; // Convert back to [-1,1]
          const songEnergy = song.energy * 2 - 1;
          const distance = Math.sqrt(
            Math.pow(songValence - valence, 2) + 
            Math.pow(songEnergy - energy, 2)
          );
          
          if (distance <= radius) {
            songs.push(song);
          }
        });
      }
    }
  }
  
  // Sort by rank and remove duplicates
  return songs
    .filter((song, index, self) => 
      index === self.findIndex(s => s.name === song.name && s.artist === song.artist)
    )
    .sort((a, b) => a.rank - b.rank);
};

/**
 * Create a heatmap with emotional quadrant labels
 */
export const createEmotionalHeatmapData = (
  audioFeatures: any[],
  gridSize: number = 80, // Increased for ultra-smooth heatmap
  smoothingSigma: number = 4.0, // Much higher sigma for organic blending
  rankWeightingFactor: number = 1.0 // 0 = equal weights, 1 = balanced, >1 = more rank influence
) => {
  const { heatmapCells, unifiedGrid } = computeGroupedDensityHeatmap(audioFeatures, gridSize, smoothingSigma, rankWeightingFactor);
  
  // Create matrix directly from unified grid to avoid coordinate transformation issues
  
  // Extract density matrix directly from unified grid (no coordinate transformations)
  const densityMatrix: number[][] = unifiedGrid.map(row => row.map(cell => cell.density));
  
  // Apply Gaussian smoothing
  const smoothedMatrix = gaussianFilter(densityMatrix, smoothingSigma);
  
  return {
    matrix: smoothedMatrix, // Use the smoothed matrix directly
    cells: heatmapCells,
    unifiedGrid, // Return unified grid instead of spatial grid
    quadrants: {
      topLeft: { label: "Angry/Tense", x: -0.5, y: 0.5 },      // Low valence, high energy
      topRight: { label: "Happy/Excited", x: 0.5, y: 0.5 },    // High valence, high energy
      bottomLeft: { label: "Sad/Depressed", x: -0.5, y: -0.5 }, // Low valence, low energy
      bottomRight: { label: "Calm/Relaxed", x: 0.5, y: -0.5 }   // High valence, low energy
    }
  };
};